/**
 * NavigatorX Waitlist Manager
 * 
 * Comprehensive waitlist management for launch:
 * - Signup tracking with state breakdown
 * - Source attribution
 * - Invite management (single & bulk)
 * - State-by-state launch control
 * - Conversion tracking
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import {
  Users,
  Mail,
  Send,
  Download,
  Search,
  RefreshCw,
  Loader2,
  MapPin,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Trash2,
  Link,
  Globe,
  Filter,
  ChevronDown,
  Rocket
} from 'lucide-react';
import { toast } from 'sonner';
import { Timestamp } from 'firebase/firestore';
import { auth } from '../../../utils/firebase/client';
import {
  getWaitlistEntries,
  getWaitlistStats,
  sendInvite,
  sendBulkInvites,
  deleteWaitlistEntry,
  exportWaitlist,
  getLaunchedStates,
  setStateLaunchStatus,
  type WaitlistEntry,
  type WaitlistStats
} from '../../../utils/services/navigatorXWaitlistService';

interface WaitlistManagerProps {
  embedded?: boolean;
}

// US States for Oklahoma and surrounding
const STATES = [
  { code: 'OK', name: 'Oklahoma', launched: true },
  { code: 'TX', name: 'Texas', launched: false },
  { code: 'KS', name: 'Kansas', launched: false },
  { code: 'NM', name: 'New Mexico', launched: false },
  { code: 'CO', name: 'Colorado', launched: false },
  { code: 'AR', name: 'Arkansas', launched: false },
  { code: 'LA', name: 'Louisiana', launched: false },
  { code: 'OTHER', name: 'Other States', launched: false }
];

export default function WaitlistManager({ embedded = false }: WaitlistManagerProps) {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [stats, setStats] = useState<WaitlistStats | null>(null);
  const [launchedStates, setLaunchedStates] = useState<string[]>(['OK']);
  
  // Filters
  const [stateFilter, setStateFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  const adminId = auth.currentUser?.uid || 'unknown';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [entriesData, statsData, launched] = await Promise.all([
        getWaitlistEntries(),
        getWaitlistStats(),
        getLaunchedStates()
      ]);
      
      setEntries(entriesData);
      setStats(statsData);
      setLaunchedStates(launched);
    } catch (error) {
      console.error('Error loading waitlist:', error);
      toast.error('Failed to load waitlist data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async (entry: WaitlistEntry) => {
    try {
      await sendInvite(entry.id!, adminId);
      toast.success(`Invite sent to ${entry.email}`);
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to send invite');
    }
  };

  const handleBulkInvite = async () => {
    if (selectedIds.size === 0) {
      toast.error('No entries selected');
      return;
    }
    
    try {
      await sendBulkInvites(Array.from(selectedIds), adminId);
      toast.success(`Invites sent to ${selectedIds.size} users`);
      setSelectedIds(new Set());
      setSelectAll(false);
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to send bulk invites');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this waitlist entry?')) return;
    
    try {
      await deleteWaitlistEntry(id, adminId);
      toast.success('Entry deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleExport = async () => {
    try {
      const csv = await exportWaitlist(filteredEntries);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `waitlist_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Waitlist exported');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleStateLaunch = async (stateCode: string, launch: boolean) => {
    try {
      await setStateLaunchStatus(stateCode, launch, adminId);
      toast.success(`${stateCode} ${launch ? 'launched' : 'unlaunched'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update state status');
    }
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredEntries.map(e => e.id!)));
    }
    setSelectAll(!selectAll);
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const formatDate = (ts: any) => {
    if (!ts) return 'N/A';
    const d = ts instanceof Timestamp ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString();
  };

  // Filter entries
  const filteredEntries = entries.filter(entry => {
    if (stateFilter !== 'all' && entry.state !== stateFilter) return false;
    if (sourceFilter !== 'all' && entry.source !== sourceFilter) return false;
    if (statusFilter === 'invited' && !entry.invitedAt) return false;
    if (statusFilter === 'pending' && entry.invitedAt) return false;
    if (statusFilter === 'converted' && !entry.convertedAt) return false;
    if (searchTerm && !entry.email.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Get unique sources for filter
  const sources = [...new Set(entries.map(e => e.source).filter(Boolean))];

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-6'}>
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Waitlist Manager</h2>
            <p className="text-gray-500">Manage pre-launch signups and invites</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      )}

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Total Signups</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Users className="h-6 w-6 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <Clock className="h-6 w-6 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Invited</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.invited}</p>
                </div>
                <Mail className="h-6 w-6 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Converted</p>
                  <p className="text-2xl font-bold text-green-600">{stats.converted}</p>
                </div>
                <CheckCircle className="h-6 w-6 text-green-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Conversion Rate</p>
                  <p className="text-2xl font-bold">{stats.conversionRate}%</p>
                </div>
                <TrendingUp className="h-6 w-6 text-green-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Launch Control & By State */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* State Launch Control */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Rocket className="h-5 w-5" />
              State Launch Control
            </CardTitle>
            <CardDescription>Enable or disable launch by state</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {STATES.map(state => (
                <div key={state.code} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">{state.name}</span>
                    <Badge variant="outline">{state.code}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {stats?.byState[state.code] && (
                      <span className="text-sm text-gray-500">
                        {stats.byState[state.code]} signups
                      </span>
                    )}
                    <Switch
                      checked={launchedStates.includes(state.code)}
                      onCheckedChange={(checked) => handleStateLaunch(state.code, checked)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* By Source */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Signups by Source
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.bySource && Object.keys(stats.bySource).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.bySource)
                  .sort((a, b) => b[1] - a[1])
                  .map(([source, count]) => (
                    <div key={source} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="capitalize">{source || 'Direct'}</span>
                          <span className="text-gray-500">{count}</span>
                        </div>
                        <div className="w-full h-2 bg-gray-100 rounded-full">
                          <div
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${(count / stats.total) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4">No source data available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Waitlist Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Waitlist Entries</CardTitle>
            {selectedIds.size > 0 && (
              <Button onClick={handleBulkInvite}>
                <Send className="h-4 w-4 mr-2" />
                Invite Selected ({selectedIds.size})
              </Button>
            )}
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-48"
              />
            </div>
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="all">All States</option>
              {STATES.map(s => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="all">All Sources</option>
              {sources.map(s => (
                <option key={s} value={s}>{s || 'Direct'}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="invited">Invited</option>
              <option value="converted">Converted</option>
            </select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th className="text-left p-2">Email</th>
                  <th className="text-left p-2">State</th>
                  <th className="text-left p-2">Source</th>
                  <th className="text-left p-2">Signed Up</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500">
                      No entries found
                    </td>
                  </tr>
                ) : (
                  filteredEntries.map(entry => (
                    <tr key={entry.id} className="border-b hover:bg-gray-50">
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(entry.id!)}
                          onChange={() => toggleSelect(entry.id!)}
                        />
                      </td>
                      <td className="p-2 font-medium">{entry.email}</td>
                      <td className="p-2">
                        <Badge variant="outline">{entry.state || 'N/A'}</Badge>
                      </td>
                      <td className="p-2 text-gray-500">{entry.source || 'Direct'}</td>
                      <td className="p-2 text-gray-500">{formatDate(entry.createdAt)}</td>
                      <td className="p-2">
                        {entry.convertedAt ? (
                          <Badge className="bg-green-100 text-green-700">Converted</Badge>
                        ) : entry.invitedAt ? (
                          <Badge className="bg-blue-100 text-blue-700">Invited</Badge>
                        ) : (
                          <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>
                        )}
                      </td>
                      <td className="p-2">
                        <div className="flex gap-1">
                          {!entry.invitedAt && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSendInvite(entry)}
                            >
                              <Send className="h-3 w-3" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                            onClick={() => handleDelete(entry.id!)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 text-sm text-gray-500">
            Showing {filteredEntries.length} of {entries.length} entries
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
